// SidebarBest.js
import React, { useContext, useEffect, useState } from "react";

import { GalleryVerticalEnd, ChevronsUpDown, LogOut, Menu } from 'lucide-react';
import { getDashboardSidebarLinks, DASHBOARD_SIDEBAR_BOTTOM_LINKS } from '../../lib/consts/navigation';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import classnames from 'classnames';
import { UserContext } from "../../context/UserContext";
import { getVenueBrandName } from "../../lib/consts/venueTypes";
import axios from 'axios';
import { alCambiarCarritos } from '../../lib/carritosBus';
import {
  alCambiarSmack,
  alCambiarSmackUnread,
  publicarSmackUnread,
  alCambiarSmackPendientes,
  publicarSmackPendientes,
} from '../../lib/smackBus';
import { cerrarSesion } from "../../lib/authSession";

function normalizeRoleLabel(role) {
  const raw = (role || '').toString().toLowerCase();
  const norm = raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '');
  if (norm.includes('superadmin') || norm.includes('superadministr')) return 'Super Admin';
  if (norm === 'owner' || norm === 'propietario') return 'Propietario';
  if (norm === 'staff' || norm === 'personal') return 'Personal';
  return role;
}

// Estos dos contadores viven en el sidebar, o sea que se pollean en TODAS las páginas,
// para todos los usuarios logueados, permanentemente. A 10 s eran ~12 peticiones/minuto
// por sesión, y cada una arrastra la query de autenticación (que carga el usuario entero).
// Era, con diferencia, la mayor fuente de carga de la API.
//
// A 60 s se mantiene la frescura porque el refresco de verdad es por eventos: al enfocar
// la ventana, al volver a la pestaña, y —para preinscripciones— por el bus
// `prereg-count-updated` que dispara la propia página al aprobar/rechazar.
const PREREG_COUNT_POLL_MS = 60000;
const CARTS_COUNT_POLL_MS = 60000;
const SMACK_UNREAD_POLL_MS = 60000;

export default function SidebarBest({ isOpen, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const userCtx = useContext(UserContext);
  const [token, setToken, , venueId, , , , accessPrivileges, , , , venueType, , , , , venueOrdersEnabled] = userCtx;
  // Posición 18 del array posicional (candado ComanderoMenuSinRecargar): flag de
  // Smack del venue. Se lee por índice, como recomienda el propio contexto.
  const venueSmackEnabled = Boolean(userCtx[18]);
  const userRole = accessPrivileges || null;
  const brandName = getVenueBrandName(venueType);

  const [preregCount, setPreregCount] = useState(0);
  const [cartsCount, setCartsCount] = useState(0);
  const [smackUnread, setSmackUnread] = useState(0);
  const [smackPendientes, setSmackPendientes] = useState(0);
  // El superadmin ve Smack siempre (canales internos del equipo, sin venue).
  const smackVisible = venueSmackEnabled || userRole === 'superadmin';

  useEffect(() => {
    if (!venueId || !token) return;

    const refreshPreregCount = () => {
      axios.get(`/v1/venues/${venueId}/pre-registrations/count`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, max-age=0',
          Pragma: 'no-cache',
        },
        params: { _ts: Date.now() },
      }).then(r => setPreregCount(r.data.pending || 0)).catch(() => {});
    };

    const handlePreregCountUpdated = (event) => {
      const nextPending = Number(event?.detail?.pending);
      if (!Number.isNaN(nextPending)) {
        setPreregCount(nextPending);
        return;
      }
      refreshPreregCount();
    };

    const handleWindowFocus = () => refreshPreregCount();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshPreregCount();
      }
    };

    refreshPreregCount();
    const interval = setInterval(() => {
      // Pestaña en segundo plano: no gastar peticiones. Al volver, el listener de
      // `visibilitychange` de abajo refresca al instante, así que no se pierde frescura.
      if (document.visibilityState !== 'visible') return;
      refreshPreregCount();
    }, PREREG_COUNT_POLL_MS);

    window.addEventListener('prereg-count-updated', handlePreregCountUpdated);
    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearInterval(interval);
      window.removeEventListener('prereg-count-updated', handlePreregCountUpdated);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [venueId, token, location.pathname]);

  useEffect(() => {
    if (!venueId || !token) return;

    const refreshCartsCount = () => {
      axios.get('/v1/venue-draft-carts', {
        params: { venue_id: venueId, _ts: Date.now() },
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, max-age=0',
          Pragma: 'no-cache',
        },
      })
        .then((r) => setCartsCount(Array.isArray(r.data?.draft_carts) ? r.data.draft_carts.length : 0))
        .catch(() => {});
    };

    const handleWindowFocus = () => refreshCartsCount();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshCartsCount();
      }
    };

    refreshCartsCount();
    // Aviso directo de quien acaba de tocar un carrito: sin esto el número esperaba
    // al siguiente sondeo (hasta un minuto) porque navegar dentro de la app no
    // dispara ni `focus` ni `visibilitychange` — la pestaña nunca se fue.
    const dejarDeEscuchar = alCambiarCarritos(refreshCartsCount);
    const interval = setInterval(() => {
      // Igual que el contador de preinscripciones: en segundo plano no se pollea.
      if (document.visibilityState !== 'visible') return;
      refreshCartsCount();
    }, CARTS_COUNT_POLL_MS);

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearInterval(interval);
      dejarDeEscuchar();
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [venueId, token, location.pathname]);

  // Contador de no-leídos de Smack. Mismo régimen que los otros dos (60 s +
  // visibilityState + focus/visibilitychange) y refresco inmediato por el bus
  // cuando esta misma sesión envía o lee. ÚNICO poller de este contador: el
  // total se re-publica por 'wa:smack-unread' para que MobileBottomNav pinte
  // su badge sin pedir nada a la API.
  useEffect(() => {
    if (!token || !smackVisible) return;

    const refreshSmackUnread = () => {
      axios.get('/v1/smack/unread-count', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, max-age=0',
          Pragma: 'no-cache',
        },
        params: { _ts: Date.now() },
      }).then((r) => {
        const total = Number(r.data?.total_unread) || 0;
        setSmackUnread(total);
        publicarSmackUnread(total);
      }).catch(() => {});
    };

    const handleWindowFocus = () => refreshSmackUnread();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshSmackUnread();
      }
    };

    refreshSmackUnread();
    const dejarDeEscuchar = alCambiarSmack(refreshSmackUnread);
    // SmackPage re-publica el total que ya conoce de su sondeo de 15 s: el
    // badge queda fresco dentro de Smack sin que este poller pida nada más.
    const dejarDeEscucharTotal = alCambiarSmackUnread((event) => {
      const total = Number(event?.detail?.total);
      if (!Number.isNaN(total)) setSmackUnread(total);
    });
    const interval = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      refreshSmackUnread();
    }, SMACK_UNREAD_POLL_MS);

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearInterval(interval);
      dejarDeEscuchar();
      dejarDeEscucharTotal();
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token, smackVisible, location.pathname]);

  // Contador de pendientes sin resolver de Smack (distinto de los no-leídos:
  // un mensaje puede estar leído y seguir sin resolverse). Mismo régimen que
  // el bloque de arriba — único poller, se re-publica por el bus para quien
  // pinte el badge sin sondear por su cuenta.
  useEffect(() => {
    if (!token || !smackVisible) return;

    const refreshSmackPendientes = () => {
      axios.get('/v1/smack/issues', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, max-age=0',
          Pragma: 'no-cache',
        },
        // Solo interesa open_count: `limit: 0` trae el contador sin la lista.
        // Sin él, este sondeo de 60 s se traía todos los pendientes abiertos
        // para tirarlos acto seguido.
        params: { resolved: false, limit: 0, _ts: Date.now() },
      }).then((r) => {
        const total = Number(r.data?.open_count) || 0;
        setSmackPendientes(total);
        publicarSmackPendientes(total);
      }).catch(() => {});
    };

    const handleWindowFocus = () => refreshSmackPendientes();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshSmackPendientes();
      }
    };

    refreshSmackPendientes();
    const dejarDeEscuchar = alCambiarSmack(refreshSmackPendientes);
    const dejarDeEscucharTotal = alCambiarSmackPendientes((event) => {
      const total = Number(event?.detail?.total);
      if (!Number.isNaN(total)) setSmackPendientes(total);
    });
    const interval = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      refreshSmackPendientes();
    }, SMACK_UNREAD_POLL_MS);

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearInterval(interval);
      dejarDeEscuchar();
      dejarDeEscucharTotal();
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token, smackVisible, location.pathname]);

  // Contador en el título de la pestaña: visible sin enfocar la ventana (los
  // avisos de verdad — push del sistema con sonido — llegan en la fase F-Push).
  // Limitación asumida: si otra parte reescribe el título justo después, el
  // prefijo espera al siguiente cambio de contador o de ruta.
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\+?\)\s/, '');
    document.title = smackUnread > 0 ? `(${smackUnread > 99 ? '99+' : smackUnread}) ${base}` : base;
  }, [smackUnread, location.pathname]);

  const badgeMap = { preregCount, cartsCount, smackUnread, smackPendientes };

  const handleLogout = () => {
    cerrarSesion();
    setToken(null);
    navigate('/login');
    onClose();
  };

  // `venueOrdersEnabled` viene del #471: la Barra solo aparece con el flag.
  // `link.section` son las cabeceras de grupo del sidebar de Figma — no llevan
  // `enabled`, así que sin esta condición se filtrarían.
  const allowedLinks = getDashboardSidebarLinks(venueType, venueOrdersEnabled, smackVisible).filter(link =>
    (link.section || link.enabled !== false) && (!link.roles || link.roles.includes(userRole))
  );
  const allowedBottomLinks = DASHBOARD_SIDEBAR_BOTTOM_LINKS.filter(
    link => !link.roles || link.roles.includes(userRole)
  );

  // Agrupar por sección para reproducir el Club_Sidebar de Figma: cada grupo es
  // un bloque con su título encima. Un grupo que se queda sin enlaces visibles
  // —por rol o por tipo de local— no pinta su título.
  const sections = [];
  for (const link of allowedLinks) {
    if (link.section) sections.push({ key: link.key, title: link.section, links: [] });
    else if (sections.length) sections[sections.length - 1].links.push(link);
    else sections.push({ key: '__sin-titulo', title: null, links: [link] });
  }
  const visibleSections = sections.filter(s => s.links.length > 0);

  if (!userRole) return null; // or show a loading spinner
  


  return (
    <>
      {/* Mobile backdrop */}
      <div
        className={classnames(
          "fixed inset-0 bg-black bg-opacity-50 z-[50] transition-opacity md:hidden",
          { 'opacity-100': isOpen, 'opacity-0 pointer-events-none': !isOpen }
        )}
        onClick={onClose}
      />

      {/* Sidebar drawer - z-[60] para quedar por encima del banner del socio en Dispensario/Productos */}
      <aside
        className={classnames(
          // El borde derecho lo pide el diseño (`Club_Sidebar`: 1 px de `sidebar-border`)
          // y nunca llegó al código. Hacía las veces una `shadow` que en escritorio
          // se anula (`md:shadow-none`, abajo), así que de tablet para arriba el
          // sidebar se quedaba sin nada: su #fafafa contra el #f9fafb de la página
          // es un punto de diferencia, invisible.
          "fixed inset-y-0 left-0 z-[60] flex min-h-0 w-64 flex-col border-r border-[var(--sidebar-border)] bg-[var(--sidebar)] shadow transform transition-transform duration-200 ease-in-out",
          { 'translate-x-0': isOpen, '-translate-x-full': !isOpen },
          'md:translate-x-0 md:static md:shadow-none md:h-full'
        )}
      >
        {/* Mobile header */}
        <div className="flex shrink-0 items-center justify-between px-4 py-3 md:hidden">
          <span className="text-lg font-semibold">Menu</span>
          <button onClick={onClose}>
            <Menu size={20} strokeWidth={1.5} />
          </button>
        </div>

        {/* Desktop header — icon tile + brand + role subtitle (Figma Club_Sidebar header) */}
        <div className="hidden shrink-0 border-b border-[var(--sidebar-border)] p-2 md:block">
          <div className="flex items-center gap-2 rounded-md p-2">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[10px] bg-[#171717] text-white">
              <GalleryVerticalEnd size={16} strokeWidth={1.5} />
            </div>
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-semibold leading-5 text-[#0A0A0A]">{brandName}</span>
              {userRole && (
                <span className="truncate text-xs leading-4 text-[#0A0A0A]/70">{normalizeRoleLabel(userRole)}</span>
              )}
            </div>
            <ChevronsUpDown size={16} strokeWidth={1.5} className="flex-shrink-0 text-[#0A0A0A]/60" />
          </div>
        </div>

        {/* Links: ocupa el espacio sobrante y hace scroll; el pie (logout) queda fijo abajo en móvil.
            Estructura del Club_Sidebar (State=Open) de Figma: cada grupo es un
            bloque con padding 8, su título de 32 de alto arriba, y los
            Club_Menu_button separados 4. El padding horizontal vive en el grupo,
            no en el <nav>, para que título y botones queden a la misma sangría. */}
        <nav className="min-h-0 flex-1 overflow-y-auto">
          {visibleSections.map((section, i) => (
            <div
              key={section.key}
              // La línea entre bloques no es un nodo suelto en Figma: es un borde
              // superior en cada grupo salvo el primero, con la variable
              // `sidebar-border`. Mismo criterio en el pie.
              className={classnames('p-2', { 'border-t border-[var(--sidebar-border)]': i > 0 })}
            >
              {section.title && (
                <p className="flex h-8 items-center px-2 text-xs text-[var(--sidebar-foreground)]">
                  {section.title}
                </p>
              )}
              <div className="space-y-1">
                {section.links.map(item => (
                  <SidebarLink
                    key={item.key}
                    item={item}
                    onClick={onClose}
                    badge={item.badge ? badgeMap[item.badge] : 0}
                    secondaryBadge={item.secondaryBadge ? badgeMap[item.secondaryBadge] : 0}
                  />
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Bottom links + logout */}
        <div className="shrink-0 space-y-1 border-t border-[var(--sidebar-border)] bg-[var(--sidebar)] p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          {allowedBottomLinks.map(item => (
            <SidebarLink
              key={item.key}
              item={item}
              onClick={onClose}
            />
          ))}
          <button
            onClick={handleLogout}
            className={classnames(
              "flex w-full items-center gap-2 rounded-sm px-2 py-2 text-sm text-[#DC2626]",
              "hover:bg-[var(--sidebar-hover)]"
            )}
          >
            <LogOut size={16} strokeWidth={1.5} />
            <span>Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
}

function SidebarLink({ item, onClick, badge = 0, secondaryBadge = 0 }) {
  const { pathname, search } = useLocation();
  const q = new URLSearchParams(search);

  const isActive = (() => {
    if (item.key === 'product') {
      return pathname === '/dispenser' && q.get('mode') === 'productos';
    }
    if (item.key === 'dispenser') {
      return pathname === '/dispenser' && q.get('mode') !== 'productos';
    }
    const basePath = item.path.split('?')[0];
    return pathname === basePath && !item.path.includes('?');
  })();

  return (
    <Link
      to={item.path}
      onClick={onClick}
      className={classnames(
        "flex items-center gap-2 rounded-sm px-2 py-2 text-sm",
        // Figma Club_Menu_button (3006:6938): active = filled slate pill + white
        // label; hover = slate-200 tint, and only while NOT active (otherwise the
        // light hover would wash out the dark pill). Same for every venue type.
        isActive
          ? 'bg-[var(--sidebar-active)] text-[var(--primary-foreground)] font-medium'
          : 'text-[var(--sidebar-foreground)] hover:bg-[var(--sidebar-hover)]'
      )}
    >
      {/* En Figma el icono del Club_Menu_button es 16×16 con trazo de 1px. Lucide
          viene por defecto a 24 con strokeWidth 2, que es lo que hacía que el menú
          se viera grueso. El grosor va en unidades del viewBox de 24, así que se
          escala con el icono: 1,5 × (16/24) = 1px real. El `text-lg` de antes no
          pintaba nada — lucide fija width/height como atributos, no hereda el
          font-size. Se aplica aquí y no en las 28 declaraciones para que no
          se desalineen entre ellas. */}
      <span className="flex-shrink-0">
        {React.cloneElement(item.icon, { size: 16, strokeWidth: 1.5 })}
      </span>
      <span className="truncate flex-1">{item.label}</span>
      {/* #FF5400 (acento de Smack): pendientes sin resolver, aparte del
          rojo de no-leídos — misma marca visual que el resto de Smack en pruebas. */}
      {secondaryBadge > 0 && (
        <span
          className="ml-auto flex-shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-[#FF5400] text-white text-xs font-bold flex items-center justify-center"
          title="Pendientes de Smack sin resolver"
        >
          {secondaryBadge > 99 ? '99+' : secondaryBadge}
        </span>
      )}
      {badge > 0 && (
        <span className={classnames(
          "flex-shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center",
          secondaryBadge > 0 ? "ml-1" : "ml-auto"
        )}>
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </Link>
  );
}
